<template>
  <v-card v-if="selectedCompany" flat>
    <v-card flat :class="`d-flex v-card__container bg-gray zindex-1 ${ simple ? '' : 'pb-0'}`">
      <h4 :class="[selectedCompany.isActive ? 'no-transformed' : 'notActive-line-through no-transformed']">{{ selectedCompany.name }}</h4>
      <p :class="[selectedCompany.isActive ? 'no-transformed' : 'notActive-line-through no-transformed', 'personalnumber pl-4 mt-n4']">
        {{ selectedCompany.organizationalNumber }}
      </p>
      <p
        v-if="!selectedCompany.isActive && $route && $route.name != 'CompanyNew'"
        class="inActiveCompanyText pl-9 mt-2"
      >
        Detta företag är inaktivt.
      </p>
      <v-spacer></v-spacer>
      <SimpleSearch v-show="simple && tab==3" v-model="search" @search="doSearch" @switchMode="switchMode"/>
    </v-card>

    <v-card flat class="px-6 pb-6 bg-gray" v-if="!isApproved && canUpdate">
      <div class="d-flex align-center">
        <div class="inActiveCompanyText pr-2">
          Godkänn företaget innan du kan ändra det
        </div>
        <v-btn color="secondary" small @click="onApprove">
          godkänn
        </v-btn>
      </div>
    </v-card>

    <v-card flat v-show="!simple && tab==3" class="v-card__container bg-gray zindex-1 pt-0">
      <AdvancedSearch  v-model="advSearch" :searchFields="advancedSearchFields" @search="doSearch" @switchMode="switchMode" />
    </v-card>

    <v-tabs v-model="tab">
      <v-tab key="1">Uppgifter</v-tab>
      <v-tab key="2" v-if="hideTab && isApproved" @click="reRender = !reRender">Lärlingar</v-tab>
      <v-tab key="3" v-if="hideTab && isApproved" @click="reRender = !reRender" data-cy="employee-tab">Personal</v-tab>
      <v-tab key="4" v-if="hideTab && isApproved" @click="reRender = !reRender" data-cy="workplace-tab">Arbetsplatser</v-tab>
      <v-tab key="5" v-if="hideTab && isApproved" @click="reRender = !reRender" data-cy="department-tab">Avdelningar</v-tab>
      <v-tab key="6" v-if="hideTab && isApproved" @click="reRender = !reRender" data-cy="company-notes">
        <v-badge :value="tab === 5 ? 0 : companyHasNotes" :content="'!'">
          <span class="hidden-md-and-down">Anteckningar</span>
          <span class="hidden-lg-and-up">Ant.</span>
        </v-badge>
      </v-tab>
      <v-tab key="7" v-if="hideTab && isApproved" @click="reRender = !reRender" data-cy="change-company-status">Inställningar</v-tab>
    </v-tabs>

    <!-- Include components-->
    <v-tabs-items v-model="tab">
      <v-tab-item key="1">
        <company-information
          :selectItems="contractTypes"
          :regions="regions"
          @showTabsAfterCompanyIsCreated="showTabsAfterCompanyIsCreated"
        />
      </v-tab-item>

      <v-tab-item key="2" v-if="hideTab && isApproved" >
        <CompanyApprenticeships :reRender="reRender" :allDepartments="allDepartments" />
      </v-tab-item>

      <v-tab-item key="3" v-if="hideTab && isApproved"  >
        <CompanyEmployments :reRender="reRender" />
      </v-tab-item>

      <v-tab-item key="4" v-if="hideTab && isApproved"  >
        <CompanyWorkplaces :reRender="reRender" :filters="filters" />
      </v-tab-item>

      <v-tab-item key="5" v-if="hideTab && isApproved" >
        <CompanyDepartaments :reRender="reRender" />
      </v-tab-item>

      <v-tab-item key="6" v-if="hideTab && isApproved">
        <CompanyNotes :reRender="reRender" class="mx-6 mt-6" />
      </v-tab-item>

      <v-tab-item key="7" v-if="hideTab && isApproved">
        <CompanyStatus :reRender="reRender" />
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import {CanIDo} from '@/components/mixins'
import API from '@/services/api.js'

import CompanyInformation from '@/components/company/CompanyInformation.vue'
import CompanyEmployments from '@/components/company/employments/CompanyEmployments'
import CompanyApprenticeships from '@/components/company/apprenticenship/CompanyApprenticeships'
import CompanyWorkplaces from '@/components/company/CompanyWorkplaces.vue'
import CompanyDepartaments from '@/components/company/departments/CompanyDepartaments'
import CompanyStatus from '@/components/company/CompanyStatus.vue'
import SimpleSearch from "@/components/search/Simple";
import AdvancedSearch from "@/components/search/Advanced";
import CompanyNotes from '@/components/company/CompanyNotes.vue'
import get from 'lodash/get'

// @group Company
// Company component
export default {
  name: 'Company',

  mixins: [CanIDo],

  data: () => ({
    simple: true,
    search: null,
    filters: {},
    advSearch: {},

    allDepartments: [],

    reRender: false,
    hideTab: false,
    tab: null,
    contractTypes: [],
    regions: [],
    companyHasNotes: false
  }),

  components: {
    CompanyWorkplaces,
    CompanyInformation,
    CompanyEmployments,
    CompanyApprenticeships,
    CompanyDepartaments,
    CompanyStatus,
    SimpleSearch,
    AdvancedSearch,
    CompanyNotes
  },

  /**
   * @vuese
   * Used to get selectedCompany from the Vuex Store
   */
  computed: {
    ...mapGetters('company', ['selectedCompany']),
    ...mapGetters('basic', ['basicRegions']),

    advancedSearchFields() {
      let fields = {
        workplaceSearch: {
          heading: 'Fritext',
          chipLabel: 'Fritext',
          label: "Sök fritt efter arbetsplatser vars information innehåller"
        },
        placeholder1: {
          heading: '',
          chipLabel: '',
          label: '',
          placeholder: true
        },
        workplaceAddress: {
          heading: 'Adress',
          chipLabel: 'Adress',
          label: "Sök efter arbetsplatser vars adress innehåller"
        },
        workplaceCity: {
          heading: 'Ort',
          chipLabel: 'Ort',
          label: "Sök efter arbetsplatser vars ortsnamn innehåller"
        },
        workplaceZip: {
          heading: 'Postnummer',
          chipLabel: 'Postnr',
          label: "Sök efter arbetsplatser vars postnummer innehåller"
        },
      }

      if (this.selectedCompany && this.selectedCompany.hasDepartments) {
        fields['workplaceDepartments'] = {
          heading: 'Avdelning',
          chipLabel: 'Avdelningar',
          label: "Sök efter arbetsplatser som tillhör avdelningar",
          items: this.allDepartments.map(o => { return { value: o.id, text: o.name } }),
          type: 'select',
          multiple: true
        }
      }

      return fields
    },

    isApproved(){
      return this.selectedCompany && this.selectedCompany.isApproved
    }
  },

  asyncComputed: {
    canUpdate(){
      return this.canIDo('company', 'update');
    },
  },

  /**
   * @vuese
   * Used to get FetchData , fetch contrat types , list Regions
   */
  mounted() {
    // @vuese
    //--Get the specific company from the db
    this.fetchData()
    // @vuese
    //--Get the list contrat types  from the db
    this.listContratType()
    // @vuese
    //--Get the list of Regions types  from the db
    this.listRegion()
  },
  /**
   * @vuese
   * Used to call FetchData everytime user commes back
   */
  watch: {
    $route: 'fetchData',
    tab: 'listCompanyNotes'
  },

  methods: {
    ...mapActions('company', ['setSelectedCompany']),
    onApprove() {
      API.approveCompany(this.selectedCompany.id).then(({ data }) => {
        if(data.updatedRows === 1){
          this.setSelectedCompany({
            ...this.selectedCompany,
            isApproved: true
          })
        }
      })
    },

    switchMode() {
      this.simple=!this.simple
      if (this.simple) {
        this.advSearch = {}
      } else {
        this.search = ""
      }
      this.doSearch()
    },

    doSearch() {
      this.filters = {
        advancedMode: !this.simple,
        search: this.search,
        advSearch: JSON.parse(JSON.stringify(this.advSearch))
      }
    },

    // @vuese
    //--Get the contractTypes company from the db
    listContratType() {
      API.listContractTypes()
        .then(response => {
          this.contractTypes = response.data.map(o => {
            return {value: o.id, text: o.name}
          })
        })
        .catch(error => {
          console.log('There was an error:', error.response)
        })
    },
    // @vuese
    //--Get the Regions  from the db
    listRegion() {
      this.regions = this.basicRegions.map(o => {
        return {value: o.id, text: o.name}
      })
    },
    /**
     * @vuese
     * Used to get FetchData
     * Check if the id exist then make a update otherwise create an update
     */
    async fetchData() {
      if (this.$route.params.id) {
        this.hideTab = true

        await API.getCompany(this.$route.params.id)
          .then(async res => {
            // @vuese
            // * Save companies to store
            res.data.triggerCall = false
            await this.setSelectedCompany(res.data)
          })
          .catch(error => {
            console.log('There was an error:', error.response)
          })
        
        if (this.selectedCompany.hasDepartments) {
          await API.listDepartments(this.selectedCompany.id)
            .then(res => {
              this.allDepartments = res.data
            })
            .catch(error => {
              console.log('There was an error:', error.response)
            })
        }
      } else {
        this.hideTab = false
      }
    },
    showTabsAfterCompanyIsCreated(val) {
      this.hideTab = val
    },

    listCompanyNotes() {
      if ( !get(this, '$route.params.id', null) || this.tab === 5 ) return
      return API.listNotes ({
        startAt: 0,
        sortBy: 'createdAt',
        sortDesc: 'DESC',
        search: '',
        advSearch: { notableType: 'Company', companyId: get(this, '$route.params.id', null) },
        pageSize: 1
      })
      .then(response => this.companyHasNotes = response?.data?.count > 0)
      .catch(error => console.log('There was an error:', error.response || error))
    }
  }
}
</script>

<style scoped>
</style>
